import React from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import SongStateProvider, { useSongs } from '../../components/songs/SongStateProvider';
import SongFilterBar from './SongFilterBar';
import SongTable from './SongTable';
import SongFormModal from '../../components/songs/SongFormModal';
import MobileSongLibrary from '../../components/MobileSongLibrary';
import { useNavigation } from "../../contexts/NavigationContext";
import { useLanguage } from '../../contexts/LanguageContext';

// Internal component that uses the context
const SongManagerContent = () => {
  const { t } = useLanguage();
  const { isMobile } = useNavigation();
  const {
    loading,
    error,
    filteredSongs,
    songs,
    selectedOwner,
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    categories,
    availableTags,
    tagFilter,
    setTagFilter,
    handleOwnerChange,
    handleOpenSongModal,
    handleDelete,
    currentSong,
    // Pass the rest to child components
    ...songContextProps
  } = useSongs();
  
  // If mobile, render the mobile-optimized version
  if (isMobile) {
    return <MobileSongLibrary />;
  }
  
  // Show loading spinner when initially loading
  if (loading && songs.length === 0) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">{t('songbook.loading')}</span>
        </Spinner>
      </div>
    );
  }
  
  return (
    <Card className="shadow-sm border-0 rounded-4">
      <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{t('songbook.title')}</h4>
        <Button
          variant="primary"
          className="rounded-pill"
          onClick={() => handleOpenSongModal()}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" /> {t('songbook.addNewSong')}
        </Button>
      </Card.Header>
      
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        {/* Filter bar component with the new owner selector */}
        <SongFilterBar 
          selectedOwner={selectedOwner}
          onOwnerChange={handleOwnerChange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
          availableTags={availableTags}
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          getSongCountByOwnership={songContextProps.getSongCountByOwnership}
        />
        
        {/* Song table component */}
        <SongTable
          filteredSongs={filteredSongs}
          handleOpenModal={handleOpenSongModal}
          handleDelete={handleDelete}
          loading={loading}
          error={error}
          {...songContextProps}
        />
        
        {/* Modals */}
        <SongFormModal
          show={songContextProps.showSongModal}
          handleClose={() => songContextProps.setShowSongModal(false)}
          onSongCreated={(song) => {
            songContextProps.refreshSongs();
            songContextProps.setShowSongModal(false);
          }}
          onSongUpdated={(song) => {
            songContextProps.refreshSongs();
            songContextProps.setShowSongModal(false);
          }}
        />
        
      </Card.Body>
    </Card>
  );
};

// Main component that wraps the context provider
const SongManagement = ({ onCountsUpdate }) => {
  return (
    <SongStateProvider onCountsUpdate={onCountsUpdate}>
      <SongManagerContent />
    </SongStateProvider>
  );
};

export default SongManagement;